/**
 * @fileoverview Modal para gestionar prioridades de tareas de operarios
 * Permite reordenar tareas mediante botones
 */

import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';

export const ModalGestionPrioridades = ({ isOpen, toggle, operario, onGuardar }) => {
  const [tareas, setTareas] = useState([]);
  const [tareasOriginales, setTareasOriginales] = useState([]);
  const [error, setError] = useState('');

  // Inicializar tareas al abrir el modal
  useEffect(() => {
    if (operario && operario.tareas) {
      // Filtrar solo las tareas que se pueden iniciar (ready === true)
      const tareasDisponibles = operario.tareas.filter(tarea => tarea.ready === true);
      
      // Ordenar tareas por prioridad
      const tareasOrdenadas = [...tareasDisponibles].sort((a, b) => 
        (a.prioridad || 0) - (b.prioridad || 0)
      );
      setTareas(tareasOrdenadas);
      // Guardar las tareas originales para comparar cambios
      setTareasOriginales(tareasOrdenadas.map(t => ({ id: t.id, prioridad: t.prioridad })));
    }
  }, [operario]);

  // Mover tarea hacia arriba (aumentar prioridad)
  const moverArriba = (index) => {
    if (index <= 0) return; // Ya está en la primera posición
    
    // Crear una copia del array de tareas
    const nuevasTareas = [...tareas];
    
    // Intercambiar posiciones
    [nuevasTareas[index], nuevasTareas[index - 1]] = [nuevasTareas[index - 1], nuevasTareas[index]];
    
    // Actualizar prioridades basadas en el nuevo orden
    const tareasConPrioridad = nuevasTareas.map((tarea, idx) => ({
      ...tarea,
      prioridad: idx + 1
    }));
    
    // Actualizar estado
    setTareas(tareasConPrioridad);
  };
  
  // Mover tarea hacia abajo (disminuir prioridad)
  const moverAbajo = (index) => {
    if (index >= tareas.length - 1) return; // Ya está en la última posición
    
    // Crear una copia del array de tareas
    const nuevasTareas = [...tareas];
    
    // Intercambiar posiciones
    [nuevasTareas[index], nuevasTareas[index + 1]] = [nuevasTareas[index + 1], nuevasTareas[index]];
    
    // Actualizar prioridades basadas en el nuevo orden
    const tareasConPrioridad = nuevasTareas.map((tarea, idx) => ({
      ...tarea,
      prioridad: idx + 1
    }));
    
    // Actualizar estado
    setTareas(tareasConPrioridad);
  };

  const handleGuardar = () => {
    try {
      // Verificar si hay tareas con prioridad 1 (necesitan reordenamiento)
      const tieneTareasConPrioridad1 = tareas.some(tarea => tarea.prioridad === 1);
      
      // Preparar todas las tareas con sus nuevas prioridades según el orden visual
      const tareasReordenadas = tareas.map((tarea, index) => ({
        id: tarea.id,
        prioridad: index + 1
      }));

      // Si hay tareas con prioridad 1, siempre actualizar para asignar prioridades secuenciales
      if (tieneTareasConPrioridad1) {
        console.log('Hay tareas con prioridad 1, reordenando todas las prioridades');
        onGuardar(tareasReordenadas);
        return;
      }

      // Si no hay tareas con prioridad 1, verificar si realmente hay cambios
      const tareasConCambios = [];
      
      tareas.forEach((tarea, index) => {
        const tareaOriginal = tareasOriginales.find(t => t.id === tarea.id);
        const nuevaPrioridad = index + 1;
        
        // Solo incluir si la prioridad realmente cambió
        if (tareaOriginal && tareaOriginal.prioridad !== nuevaPrioridad) {
          tareasConCambios.push({
            id: tarea.id,
            prioridad: nuevaPrioridad
          });
        }
      });

      // Si no hay cambios, no hacer nada
      if (tareasConCambios.length === 0) {
        console.log('No hay cambios en las prioridades, cerrando modal');
        toggle();
        return;
      }

      // Llamar a la función de guardar solo con los cambios
      onGuardar(tareasConCambios);
    } catch (err) {
      setError('Error al guardar las prioridades');
      console.error('Error al guardar prioridades:', err);
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (!isNaN(fromIndex) && fromIndex !== toIndex) {
      // Crear una copia del array de tareas
      const nuevasTareas = [...tareas];
      
      // Intercambiar posiciones
      [nuevasTareas[fromIndex], nuevasTareas[toIndex]] = [nuevasTareas[toIndex], nuevasTareas[fromIndex]];
      
      // Actualizar prioridades basadas en el nuevo orden
      const tareasConPrioridad = nuevasTareas.map((tarea, idx) => ({
        ...tarea,
        prioridad: idx + 1
      }));
      
      // Actualizar estado
      setTareas(tareasConPrioridad);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  return (
    <>
      <style>
        {`
          .tarea-item {
            transition: all 0.2s ease;
            user-select: none;
          }
          .tarea-item:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            transform: translateY(-1px);
          }
          .tarea-item.dragging {
            opacity: 0.5;
            transform: rotate(5deg);
            box-shadow: 0 4px 12px rgba(0,0,0,0.2);
          }
          .tarea-item.drag-over {
            border-color: #007bff !important;
            background-color: #f8f9fa !important;
            transform: scale(1.02);
          }
          .tarea-item .fa-grip-vertical {
            opacity: 0.6;
            transition: opacity 0.2s ease;
          }
          .tarea-item:hover .fa-grip-vertical {
            opacity: 1;
          }
        `}
      </style>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Gestionar Prioridades - {operario?.name}
        </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        {tareas.length === 0 ? (
          <Alert color="info">No hay tareas para gestionar</Alert>
        ) : (
          <>
            <div className="alert alert-info mb-3">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Instrucciones:</strong> Arrastra las tareas para reordenarlas o usa los botones ↑ ↓ para mover una posición.
            </div>
          <div className="tareas-list">
            {tareas.map((tarea, index) => (
              <div
                key={tarea.id}
                className="tarea-item mb-2 p-3 border rounded"
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDragEnter={handleDragEnter}
                style={{ cursor: 'move' }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span className="badge bg-primary me-2">#{tarea.prioridad}</span>
                    <strong>
                      {tarea.expand?.orden?.nombre_producto || 'Producto'} - Orden #
                      {tarea.expand?.orden?.numero || tarea.orden}
                    </strong>
                  </div>
                  <div>
                    <i className="fas fa-grip-vertical text-muted me-2" title="Arrastra para reordenar"></i>
                    <Button 
                      color="secondary" 
                      size="sm" 
                      className="me-1" 
                      onClick={() => moverArriba(index)}
                      disabled={index === 0}
                    >
                      ↑
                    </Button>
                    <Button 
                      color="secondary" 
                      size="sm" 
                      onClick={() => moverAbajo(index)}
                      disabled={index === tareas.length - 1}
                    >
                      ↓
                    </Button>
                  </div>
                </div>
                <div className="mt-1 text-muted">
                  <small>{tarea.expand?.centro_costo?.nombre || 'Centro de Costo'}</small>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleGuardar}
          disabled={tareas.length === 0}
        >
          Guardar
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default ModalGestionPrioridades;
